// src/pages/SmartyList.jsx
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate, Link, useLocation} from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import useFetch from '../../hooks/useFetch';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import { logout } from '../../redux/actions/authActions';
import { getViewType } from '../../components/utils/smartyUtil';


// Helper to get default fetch filters based on view
const getDefaultFilters = (view, userId) => {
  const today = new Date();
  const startOfToday = new Date(today.setHours(0, 0, 0, 0));
  const endOfToday = new Date(today.setHours(23, 59, 59, 999));
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  
  const base = { userId };
  switch (view) {
    case 'today':
      return { ...base, itemDateFrom: startOfToday, itemDateTo: endOfToday };
    case 'month':
      return { ...base, itemDateFrom: startOfMonth, itemDateTo: endOfToday };
    case 'expired':
      return { ...base, status: 'Expired' };
    case 'expiring':
      return { ...base, status_ne: 'Expired', expiryDate_lt: endOfToday }; // today > expiryDate
    case 'location':
    case 'by-storage':
      return { ...base }; // Filters applied client-side or via search
    case 'condition':
      return { ...base };
    case 'categories':
      return { ...base };
    case 'byfamily':
      return { ...base };
    case 'predictive':
      return { ...base, status: 'Stock-In', expiryDate_gt: startOfToday }; // today < expiryDate
    case 'waste-analysis':
      return { ...base, status: 'Wasted' };
    case 'savings':
      return { ...base, status_ne: 'Expired' };
    case 'inventory':
      return { ...base, status: 'In-Stock' };
    case 'consumed':
      return { ...base, status: 'Consumed' };
    case 'wasted':
      return { ...base, $or: [{ status: 'Expired' }, { status: 'Stock-In', itemDate_lt: endOfToday }] };
    case 'given':
      return { ...base, status: 'Donated' };
    default:
      return { ...base };
  }
};

// Helper to calculate totals based on view
const calculateTotals = (items, view) => {
  let totalAmount = 0;
  let totalWasteAmount = 0;
  items.forEach(item => {
    if (view === 'savings' || view === 'inventory' || view === 'consumed' || view === 'given') {
      if (item.status !== 'Expired') totalAmount += item.amount || 0;
    } else if (view === 'given') {
      totalWasteAmount += item.wasteAmount || 0;
    } else {
      totalAmount += item.amount || 0;
    }
  });
  return { totalAmount, totalWasteAmount };
};

const SmartyList = ({ view: propView }) => {
  const authState = useSelector(state => state.authReducer);
  const { isLoggedIn, user, token } = authState;
  const userName = user?.name || "User";
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [items, setItems] = useState([]);
  const [fetchData, { loading }] = useFetch();

  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [tags, setTags] = useState('');
  const [selectedItemId, setSelectedItemId] = useState(null);

  const [itemToDelete, setItemToDelete] = useState(null);
  const [category, setCategory] = useState("");

  const location = useLocation();
  const view = getViewType(location.pathname, propView);
 
  const currentView = getViewType(location.pathname, view);

  const handleLogout = () => {
    // dispatch(logout());
    navigate("/smarty");
  };

  const formatDateTime = (date) => {
    if (!date) return 'N/A';
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
  };

  const fetchItems = useCallback(() => {
    if (!isLoggedIn) return;

    const config = {
      url: "/smartyitems/mysmartyitems",
      method: "post",
      headers: { Authorization: token },
      data: { userId: user._id }
    };
    fetchData(config, { showSuccessToast: false }).then(data => setItems(data.smartyItems || []));
  }, [fetchData, isLoggedIn, token, user]);

  useEffect(() => {
    document.title = isLoggedIn ? `${userName}'s Smarty Items` : "Think5Whys";
    fetchItems();
  }, [isLoggedIn, userName, fetchItems]);

  const handleView = (itemId) => setSelectedItemId(prev => prev === itemId ? null : itemId);
  const confirmDelete = (itemId) => setItemToDelete(itemId);
  const cancelDelete = () => setItemToDelete(null);

  const handleDelete = async () => {
    if (!itemToDelete) return;
    const config = {
      url: `/smartyitems/${itemToDelete}`,
      method: "delete",
      headers: { Authorization: token }
    };
    await fetchData(config);
    setItemToDelete(null);
    fetchItems();
  };

  const handleSearch = () => {
    const filteredItems = items.filter(item => {
      const tagMatch = !tags || (item.tags && item.tags.includes(tags));
      const fromMatch = !fromDate || new Date(item.itemDate) >= fromDate;
      const toMatch = !toDate || new Date(item.itemDate) <= toDate;
      return tagMatch && fromMatch && toMatch;
    });
    setItems(filteredItems);
  };

  const sortedItems = [...items].sort((a, b) => new Date(b.itemDate) - new Date(a.itemDate));

  return (
    <div className="min-h-screen flex flex-col bg-black text-blue-100 font-sans">
      <header className="sticky top-0 z-50 bg-black border-b border-blue-800 px-4 py-2 flex items-center justify-between shadow-lg">
        <div className="text-xl lg:text-2xl font-bold text-yellow-200">Smarty Data</div>
        <div className="hidden md:flex items-center gap-3">
          {isLoggedIn ? (
            <>
              <div className="text-xs text-blue-300">{userName}</div>
              <button
                onClick={() => navigate("/smarty")}
                className="w-10 p-2 mt-2 rounded-md bg-yellow-100 border border-blue-600 hover:bg-blue-700 transition"
                title="Home"
              >
                🔙
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate("/login")}
                className="px-3 py-1 rounded-full border border-blue-700 text-sm text-blue-200 hover:bg-blue-900"
              >
                Login
              </button>
            </>
          )}
        </div>
      </header>

      {/* Filters */}
      <div className="flex-1 bg-black py-1">
        <div className="flex flex-col items-start justify-start gap-4 bg-black border border-gray-700 shadow-lg rounded-xl p-4 mx-auto w-full max-w-md sm:max-w-3xl">
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            <div className="flex items-center gap-2 flex-1">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-pink-400 flex-shrink-0" />
              <DatePicker 
                selected={fromDate} 
                onChange={setFromDate} 
                placeholderText="From Date" 
                className="bg-gray-800 text-pink-300 px-3 py-1 rounded w-full focus:ring-2 focus:ring-pink-400 focus:outline-none" 
              />
            </div>
            <div className="flex items-center gap-2 flex-1">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-pink-400 flex-shrink-0" />
              <DatePicker 
                selected={toDate} 
                onChange={setToDate} 
                placeholderText="To Date" 
                className="bg-gray-800 text-pink-300 px-3 py-1 rounded w-full focus:ring-2 focus:ring-pink-400 focus:outline-none" 
              />
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-3 w-full">
            <div className="w-full sm:w-auto flex-1 sm:flex-none">
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="bg-gray-800 text-pink-300 px-3 py-2 h-10 rounded w-full focus:ring-2 focus:ring-pink-400 focus:outline-none"
              >
                <option value="">All Categories</option>
                <optgroup label="Fresh & Perishables">
                  <option value="vegetables">Vegetables</option>
                  <option value="fruits_nuts">Fruits & Nuts</option>
                  <option value="non_veg">Meat / Fish / Eggs</option>
                  <option value="dairy">Dairy Products</option>
                  <option value="bakery">Bread & Bakery</option>
                </optgroup>
                <optgroup label="Grocery & Staples">
                  <option value="grocery">General Grocery</option>
                  <option value="spices">Spices & Masalas</option>
                  <option value="oils_ghee">Cooking Oils & Ghee</option>
                  <option value="pulses">Pulses / Lentils</option>
                  <option value="rice_wheat_grains">Rice / Wheat / Grains</option>
                </optgroup>
                <optgroup label="Snacks & Beverages">
                  <option value="snacks_sweets">Snacks & Sweets</option>
                  <option value="tea_coffee">Tea / Coffee</option>
                  <option value="juices_drinks">Juices & Beverages</option>
                </optgroup>
                <optgroup label="Health / Toiletries / Cleaning">
                  <option value="medicines">Medicines</option>
                  <option value="cleaning_supplies">Cleaning Supplies</option>
                  <option value="toiletries">Toiletries</option>
                  <option value="cosmetics">Cosmetics</option>
                </optgroup>
                <optgroup label="Baby & Pet Items">
                  <option value="baby_food">Baby Food & Formula</option>
                  <option value="pet_food">Pet Food</option>
                </optgroup>
                <optgroup label="Refrigerated & Packaged Foods">
                  <option value="frozen_foods">Frozen Foods</option>
                  <option value="ready_to_eat">Ready-to-Eat / Packaged Meals</option>
                  <option value="canned_items">Canned / Tinned Items</option>
                </optgroup>
                <optgroup label="Others">
                  <option value="others">Others</option>
                </optgroup>
              </select>
            </div>
            
            <input 
              type="text" 
              placeholder="Enter Tags" 
              value={tags} 
              onChange={(e) => setTags(e.target.value)} 
              className="bg-gray-800 text-pink-300 px-3 py-2 rounded w-full sm:w-auto flex-1 focus:ring-2 focus:ring-pink-400 focus:outline-none" 
            />
            
            <button 
              onClick={handleSearch} 
              className="flex items-center justify-center gap-2 w-full sm:w-auto bg-pink-700 text-black font-semibold px-4 py-2 rounded hover:bg-pink-600 transition min-w-[100px]"
            >
              <FontAwesomeIcon icon={faSearch} /> Search {currentView}
            </button>
          </div>
        </div>

        <div className="text-white p-4 mt-4">
          {items.length !== 0 && (
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm mt-1 text-yellow-200">{items.length} Record(s) Found!</h3>
              <div className='flex flex-row gap-2'>
                <Link to={`/smarty-add`} className="h-10 px-4 py-2 bg-blue-700 rounded-lg flex items-center gap-2 text-white font-medium">
                  <FontAwesomeIcon icon={faPlus} /> Add New
                </Link>
                <Link to={`/smarty-upload`} className="h-10 px-4 py-2 bg-blue-700 rounded-lg flex items-center gap-2 text-white font-medium">
                  <FontAwesomeIcon icon={faPlus} /> Bulk Upload
                </Link>
              </div>
            </div>
          )}
          {loading ? <Loader /> : (
            items.length === 0 ? (
              <div className='h-[300px] flex items-center justify-center gap-4 text-yellow-300 mb-2'>
                <div className="flex flex-col items-center">
                  <h3 className='text-lg mt-1 text-orange-400'>No Record Found!</h3>
                  <div className='flex flex-row gap-2'>
                    <Link to={`/smarty-add`} className="h-10 px-4 py-2 bg-blue-700 rounded-lg flex items-center gap-2 text-white font-medium">
                      <FontAwesomeIcon icon={faPlus} /> Add New
                    </Link>
                    <Link to={`/smarty-upload`} className="h-10 px-4 py-2 bg-blue-700 rounded-lg flex items-center gap-2 text-white font-medium">
                      <FontAwesomeIcon icon={faPlus} /> Bulk Upload
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              sortedItems.map((item, index) => (
                <div key={item._id} className='bg-black border border-gray-600 py-2 px-4 text-gray-100 rounded-md shadow-md text-sm mt-4'>
                  <div className='flex items-center'>
                    <span className='font-medium'>{index + 1}. {item.title} ({formatDateTime(item.itemDate)})</span>
                    <Tooltip text="View details" position="top">
                      <span className='ml-auto mr-4 text-blue-500 cursor-pointer' onClick={() => handleView(item._id)}>
                        <i className="fa-solid fa-eye"></i>
                      </span>
                    </Tooltip>
                    <Tooltip text="Edit record" position="top">
                      <Link to={`/smarty-add/${item._id}`} className='mr-4 text-blue-600'>
                        <i className="fa-solid fa-pen"></i>
                      </Link>
                    </Tooltip>
                    <Tooltip text="Delete record" position="top">
                      <span className='text-red-500 cursor-pointer' onClick={() => confirmDelete(item._id)}>
                        <i className="fa-solid fa-trash"></i>
                      </span>
                    </Tooltip>
                  </div>
                  {selectedItemId === item._id && (
                    <div className='bg-black border border-blue-300 rounded-sm p-4 text-white mt-2'>
                      <h2 className='text-yellow-400 text-lg font-semibold mb-2 border-b border-gray-300'>Details</h2>
                      <p><strong>Is Active:</strong> {item.isActive ? "Yes" : "No"}</p>
                      <p><strong>Entry Code:</strong> {item.smartyItemCode || "N/A"}</p>
                      <p className='border-b border-gray-300'><strong>Date:</strong> {item.itemDate}</p>
                      <p><strong>Item:</strong> {item.item}</p>
                      <p><strong>Status:</strong> {item.status || "N/A"}</p>
                      <p><strong>Category:</strong> {item.category || "N/A"}</p>
                      <p><strong>Sub-category:</strong> {item.subcategory || "N/A"}</p>
                      <p><strong>Quantity:</strong> {item.qty} {item.unit}</p>
                      <p><strong>Price per Unit:</strong> {item.pricePerUnit || 0}</p>
                      <p className='border-b border-gray-300'><strong>Amount:</strong> {item.currency} {item.amount || 0}</p>

                      <p className='border-b border-gray-300'><strong>Description:</strong> {item.description || "N/A"}</p>
                      <p><strong>Quality:</strong> {item.quality || "N/A"}</p>
                      <p><strong>Storage Location:</strong> {item.storageLocation || "N/A"}</p>
                      <p><strong>Best Before:</strong> {item.bestBefore ? formatDateTime(item.bestBefore) : "N/A"}</p>
                      <p className='border-b border-gray-300'><strong>Expiry Date:</strong> {item.expiryDate ? formatDateTime(item.expiryDate) : "N/A"}</p>
                      <p><strong>Purchased From:</strong> {item.purchasedFrom || "N/A"}</p>
                      <p><strong>Vendor Type:</strong> {item.vendorType || "N/A"}</p>
                      <p className='border-b border-gray-300'><strong>Vendor Place:</strong> {item.vendorPlace || "N/A"}</p>
                      <p className='border-b border-gray-300'><strong>Entry Remarks:</strong> {item.entryRemarks || "N/A"}</p>
                      <p><strong>Waste Quantity:</strong> {item.wasteQty || "N/A"}</p>
                      <p><strong>Waste Amount:</strong> {item.wasteAmount || "N/A"}</p>
                      <p><strong>Waste Reason:</strong> {item.wasteReason || "N/A"}</p>
                      <p className='border-b border-gray-300'><strong>Responsible Person:</strong> {item.responsiblePerson || "N/A"}</p>
                      <p><strong>RCA Code:</strong> {item.rcaCode || "N/A"}</p>
                      <p className='border-b border-gray-300'><strong>Smarty Item Link Code:</strong> {item.smartyItemLinkCode || "N/A"}</p>  
                      <p><strong>Is Flagged:</strong> {item.isFlagged ? "Yes" : "No"}</p>
                      <p><strong>Is Shared:</strong> {item.isShared ? "Yes" : "No"}</p>
                      <p className='border-b border-gray-300'><strong>Shared Person Code:</strong> {item.pairCode || "N/A"}</p>
                      <p className='border-b border-gray-300'><strong>Final Remarks:</strong> {item.finalRemarks || "N/A"}</p>
                      <p><strong>Is Deleted:</strong> {item.isDeleted ? "Yes" : "No"}</p>
                      <p><strong>Date Created:</strong> {item.dateCreated ? formatDateTime(item.dateCreated) : "N/A"}</p>
                      <p><strong>Created By:</strong> {item.createdBy || "N/A"}</p>
                    </div>
                  )}

                </div>
              ))
            )
          )}
        </div>
      </div>

      {itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50">
          <div className="bg-blue-200 p-4 rounded-md flex flex-col items-center gap-3">
            <p className="text-black font-semibold">Are you sure you want to delete this item?</p>
            <div className="flex gap-4">
              <button onClick={handleDelete} className="bg-red-700 px-4 py-2 text-white rounded">Confirm Delete</button>
              <button onClick={cancelDelete} className="bg-yellow-300 px-4 py-2 text-black rounded">Cancel</button>
            </div>
          </div>
        </div>
      )}

      <footer className="bg-black border-t border-blue-800 text-white py-4 mt-auto">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs lg:text-sm">© 2025 Think5Whys Global DeepTech Solutions.</div>
          <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
            <Link to="/about-us" className="hover:text-blue-400">About</Link>
            <Link to="/privacy-policy" className="hover:text-blue-400">Privacy Policy</Link>
            <Link to="/terms-of-use" className="hover:text-blue-400">Terms of Use</Link>
            <Link to="/contact-us" className="hover:text-blue-400">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SmartyList;
